import { User } from '../../types';
import { hasUnlimitedTokens } from '../../utils/promoEffectsManager';
import { TokenConfig, getTokenConfig } from './config';

export interface TokenChargeRequest {
    operationType: 'chat' | 'quiz' | 'exam' | 'fileAnalysis';
    complexity: 'simple' | 'moderate' | 'complex' | 'advanced';
    model: 'flash' | 'pro';
    features: {
        hasAttachments?: boolean;
        attachmentCount?: number;
        attachmentSizeKB?: number;
        hasWhiteboard?: boolean;
        hasThoughtProcess?: boolean;
        usesFollowUpContext?: boolean;
        preAnalyzed?: boolean;
    };
    estimatedInputTokens?: number;
    estimatedOutputTokens?: number;
    actualInputTokens?: number;
    actualOutputTokens?: number;
}

export interface TokenChargeResult {
    charged: number;
    breakdown: {
        base: number;
        complexity: number;
        model: number;
        attachments: number;
        features: number;
        subtotal: number;
        discount: number;
        total: number;
    };
    remainingTokens: number;
    dailyUsage: number;
    dailyLimit: number;
    canProceed: boolean;
    warnings: string[];
}

export interface QuickCostEstimate {
    operationType: 'chat' | 'quiz' | 'exam' | 'fileAnalysis';
    complexity?: 'simple' | 'moderate' | 'complex' | 'advanced';
    model?: 'flash' | 'pro';
    hasAttachments?: boolean;
    attachmentCount?: number;
    attachmentSizeKB?: number;
    messageLength?: number;
    historyLength?: number;
}

/**
 * Calculate smart token charge based on request
 * Returns 0 charge for users with unlimited tokens
 */
export const calculateCharge = (request: TokenChargeRequest, user: User): TokenChargeResult => {
    const config = getTokenConfig();
    const breakdown = {
        base: 0,
        complexity: 0,
        model: 0,
        attachments: 0,
        features: 0,
        subtotal: 0,
        discount: 0,
        total: 0,
    };

    // CHECK FOR UNLIMITED TOKENS - if user has unlimited, charge 0
    if (hasUnlimitedTokens(user)) {
        console.log('[TokenSystem] User has unlimited tokens - no charge');
        return {
            charged: 0,
            breakdown,
            remainingTokens: Infinity,
            dailyUsage: user.dailyTokenUsage,
            dailyLimit: Infinity,
            canProceed: true,
            warnings: [],
        };
    }

    // 1. Base rate
    switch (request.operationType) {
        case 'chat':
            breakdown.base = request.features.hasAttachments
                ? config.baseRates.chatMessageWithAttachment
                : config.baseRates.chatMessage;
            break;
        case 'quiz':
            breakdown.base = config.baseRates.quizGeneration;
            break;
        case 'exam':
            breakdown.base = config.baseRates.examGeneration;
            break;
        case 'fileAnalysis':
            breakdown.base = config.baseRates.fileAnalysis;
            break;
    }

    // 2. Complexity multiplier
    const complexityMult = config.complexityMultipliers[request.complexity];
    breakdown.complexity = breakdown.base * (complexityMult - 1);

    // 3. Model multiplier
    const modelMult = config.modelMultipliers[request.model];
    breakdown.model = (breakdown.base + breakdown.complexity) * (modelMult - 1);

    // 4. Attachment costs
    if (request.features.hasAttachments) {
        const attachmentCount = request.features.attachmentCount || 1;
        const attachmentSizeKB = request.features.attachmentSizeKB || 0;

        // Base attachment cost
        breakdown.attachments = config.attachmentRates.imageBase; // Default

        // Size-based cost
        breakdown.attachments += attachmentSizeKB * config.attachmentRates.perKB;

        // Multiple attachments
        if (attachmentCount > 1) {
            breakdown.attachments += (attachmentCount - 1) * config.featureRates.multipleAttachments;
        }

        // Pre-analysis bonus
        if (request.features.preAnalyzed) {
            breakdown.attachments += config.attachmentRates.preAnalysisBonus;
        }
    }

    // 5. Feature costs
    if (request.features.hasWhiteboard) {
        breakdown.features += config.featureRates.whiteboardGeneration;
    }
    if (request.features.hasThoughtProcess) {
        breakdown.features += config.featureRates.thoughtProcess;
    }
    if (request.features.usesFollowUpContext) {
        breakdown.features += config.featureRates.followUpContext;
    }

    // 6. Calculate subtotal
    breakdown.subtotal =
        breakdown.base +
        breakdown.complexity +
        breakdown.model +
        breakdown.attachments +
        breakdown.features;

    // 7. Apply premium discounts
    if (user.isPremium) {
        let discountPercent = 0;
        switch (request.operationType) {
            case 'chat':
                discountPercent = config.premiumDiscounts.chatDiscount;
                break;
            case 'quiz':
                discountPercent = config.premiumDiscounts.quizDiscount;
                break;
            case 'exam':
                discountPercent = config.premiumDiscounts.examDiscount;
                break;
        }

        if (request.features.hasAttachments) {
            // Apply attachment discount to attachment portion
            const attachmentDiscount = breakdown.attachments *
                (config.premiumDiscounts.attachmentDiscount / 100);
            breakdown.discount += attachmentDiscount;
        }

        breakdown.discount += breakdown.subtotal * (discountPercent / 100);
    }

    // 8. Final total
    breakdown.total = Math.ceil(breakdown.subtotal - breakdown.discount);

    // 9. Check limits - support Standard/Premium/MAX tiers
    let dailyLimit: number;
    const isMax = (user as any).isMax || (user as any).isMaxTier;
    if (isMax) {
        dailyLimit = config.limits.maxUserDaily;
    } else if (user.isPremium) {
        dailyLimit = config.limits.premiumUserDaily;
    } else {
        dailyLimit = config.limits.standardUserDaily;
    }
    const remainingTokens = dailyLimit - user.dailyTokenUsage;
    const canProceed = breakdown.total <= remainingTokens &&
        breakdown.total <= config.limits.maxPerRequest;

    // 10. Generate warnings
    const warnings: string[] = [];
    if (breakdown.total > config.limits.maxPerRequest) {
        warnings.push(`Request exceeds maximum tokens per request (${config.limits.maxPerRequest})`);
    }
    if (breakdown.total > remainingTokens) {
        warnings.push(`Insufficient tokens. Need ${breakdown.total}, have ${remainingTokens}`);
    }
    if (user.dailyTokenUsage / dailyLimit > 0.8) {
        warnings.push(`You've used ${Math.round(user.dailyTokenUsage / dailyLimit * 100)}% of your daily limit`);
    }

    return {
        charged: breakdown.total,
        breakdown,
        remainingTokens,
        dailyUsage: user.dailyTokenUsage,
        dailyLimit,
        canProceed,
        warnings,
    };
};

/**
 * Quick cost estimate for common operations
 */
export const estimateCost = (params: QuickCostEstimate): number => {
    const config = getTokenConfig();
    const {
        operationType,
        complexity = 'moderate',
        model = 'flash',
        hasAttachments = false,
        attachmentCount = 0,
        attachmentSizeKB = 0,
        messageLength = 100,
        historyLength = 0
    } = params;

    // Base cost from config
    let baseCost = config.baseRates.chatMessage;
    switch (operationType) {
        case 'quiz':
            baseCost = config.baseRates.quizGeneration;
            break;
        case 'exam':
            baseCost = config.baseRates.examGeneration;
            break;
        case 'fileAnalysis':
            baseCost = config.baseRates.fileAnalysis;
            break;
        case 'chat':
        default:
            baseCost = hasAttachments
                ? config.baseRates.chatMessageWithAttachment
                : config.baseRates.chatMessage;
    }

    // Apply multipliers
    const complexityMult = config.complexityMultipliers[complexity];
    const modelMult = config.modelMultipliers[model];

    let totalCost = baseCost * complexityMult * modelMult;

    // Add attachment costs
    if (hasAttachments && attachmentSizeKB > 0) {
        totalCost += attachmentSizeKB * config.attachmentRates.perKB;
        totalCost += (attachmentCount - 1) * config.featureRates.multipleAttachments;
    }

    // Add context cost for longer histories
    if (historyLength > 10) {
        totalCost += config.featureRates.followUpContext * Math.floor(historyLength / 10);
    }

    return Math.ceil(totalCost);
};
